import { JSX } from 'react';
import { useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useSurvey } from 'src/contexts/SurveyContext';
import SurveySummary from 'src/components/NewRenterInterface/Survey/SurveySummary';
import { useCertificationHubBackRoute } from 'src/components/NewRenterInterface/CertificationHub/useCertificationHubBackRoute';

export function SurveyIndexPage(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    surveyId,
    actingOnMemberId,
    certId,
    certCategory,
    propertyName,
    effectiveDate,
    renterActive,
  } = useSurvey();
  const { backRoute: hubBackRoute } = useCertificationHubBackRoute({
    certificationId: certId,
    surveyId,
    userId: actingOnMemberId ?? null,
  });
  const backRoute = hubBackRoute ?? '/renter';

  const formattedDate = effectiveDate
    ? new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const headline =
    certCategory && formattedDate
      ? `${certCategory} - ${formattedDate}`
      : certCategory || formattedDate || 'Survey';

  if (!surveyId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No survey available.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          height: { xs: 80, md: 72 },
          bgcolor: 'primary.dark',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2.5, md: 5 },
          gap: 1.75,
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => navigate(backRoute)}
          aria-label="Go back"
          size="small"
          sx={{
            backgroundColor: 'common.white',
            color: 'grey.900',
            width: 34,
            height: 34,
            borderRadius: '50%',
            boxShadow: `0px 1px 4px ${alpha(
              theme.palette.text.secondary,
              0.06
            )}`,
            flexShrink: 0,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box>
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: { xs: 15, md: 16 },
              fontWeight: 600,
              letterSpacing: -0.2,
            }}
          >
            {headline}
          </Typography>
          {propertyName && (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: { xs: 12, md: 13 },
              }}
            >
              {propertyName}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <SurveySummary surveyId={surveyId} renterActive={renterActive} />
      </Box>
    </Box>
  );
}

export default SurveyIndexPage;
